import React, { useState } from 'react';
import {
    Box,
    Card,
    TextField,
    Button,
    Typography,
    Checkbox,
    FormControlLabel,
    Link,
    Container,
    IconButton,
    InputAdornment,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack, Close } from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import LogoImage from "../../assets/SJ-logo.png";
import DesignImage from "../../assets/login_design.png";
import HeroImage from "../../assets/login_hero.png";
import api from '../../api/api';
import CustomSnackbar from '../../components/CustomSnackbar';
import { useNavigate } from 'react-router-dom';
import en_ja from '../../translations/en_ja';

const theme = createTheme({
    palette: {
        primary: {
            main: '#ff0000',
        },
    },
});

const Login = () => {
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarType, setSnackbarType] = useState('success')

    const [currentScreen, setCurrentScreen] = useState('login'); // 'login', 'forgotPassword', 'enterCodeAndChangePassword'

    const [user, setUser] = useState(null);

    const [formData, setFormData] = useState({
        username: '',
        password: '',
        rememberMe: false,
        email: '',
        code: ['', '', '', '', '', ''],
        newPassword: '',
        confirmPassword: ''
    });

    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loginLoading, setLoginLoading] = useState(false);
    const navigate = useNavigate();

    const handleCloseSnackbar = () => {
        setSnackbarType('success')
        setSnackbarOpen(false);
        setSnackbarMessage('');
    }

    // Password validation function
    const validatePassword = (password) => {
        const hasCapitalLetter = /[A-Z]/.test(password);
        const hasDigit = /\d/.test(password);
        const isLongEnough = password.length >= 6;

        return {
            isValid: hasCapitalLetter && hasDigit && isLongEnough,
            hasCapitalLetter,
            hasDigit,
            isLongEnough
        };
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleCodeChange = (index, value) => {
        if (value.length <= 1) {
            const newCode = [...formData.code];
            newCode[index] = value;
            setFormData(prev => ({
                ...prev,
                code: newCode
            }));

            // Auto focus next input
            if (value && index < 5) {
                const nextInput = document.getElementById(`code-input-${index + 1}`);
                if (nextInput) nextInput.focus();
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (currentScreen === 'login') {
            if (!formData.username.trim()) {
                newErrors.username = en_ja["Username or Email is required"];
            } else if (formData.username.length < 3) {
                newErrors.username = en_ja["Username must be at least 3 characters"];
            }

            if (!formData.password.trim()) {
                newErrors.password = en_ja["Password is required"];
            } else if (formData.password.length < 6) {
                newErrors.password = en_ja["Password must be at least 6 characters"];
            }

        } else if (currentScreen === 'forgotPassword') {
            if (!formData.username.trim()) {
                newErrors.username = en_ja["Username or Email is required"];
            } else if (formData.username.length < 3) {
                newErrors.username = en_ja["Username must be at least 3 characters"];
            }

        } else if (currentScreen === 'enterCodeAndChangePassword') {
            const codeString = formData.code.join('');
            if (codeString.length !== 6) {
                newErrors.code = en_ja["Please enter all 6 digits"];
            }

            if (!formData.newPassword.trim()) {
                newErrors.newPassword = en_ja["New password is required"];
            } else {
                const passwordValidation = validatePassword(formData.newPassword);
                if (!passwordValidation.isValid) {
                    if (!passwordValidation.isLongEnough) {
                        newErrors.newPassword = en_ja["Password must be at least 6 characters"];
                    } else if (!passwordValidation.hasCapitalLetter && !passwordValidation.hasDigit) {
                        newErrors.newPassword = en_ja["Password must contain at least one capital letter and one digit"];
                    } else if (!passwordValidation.hasCapitalLetter) {
                        newErrors.newPassword = en_ja["Password must contain at least one capital letter"];
                    } else if (!passwordValidation.hasDigit) {
                        newErrors.newPassword = en_ja["Password must contain at least one digit"];
                    }
                }
            }

            if (!formData.confirmPassword.trim()) {
                newErrors.confirmPassword = en_ja["Confirm password is required"];
            } else if (formData.newPassword !== formData.confirmPassword) {
                newErrors.confirmPassword = en_ja["Passwords do not match"];
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const loginUser = async (obj) => {
        api.post("/v1/admin/admin-login", { username: obj.username, password: obj.password })
            .then((res) => {
                if (res.status === 200) {
                    setSnackbarType('success')
                    setSnackbarMessage(en_ja["Successfully Logged In"]);
                    setSnackbarOpen(true);

                    const token = res?.data?.token?.AccessToken;

                    if (token) {
                        localStorage.setItem('authToken', res?.data?.token?.AccessToken)
                        localStorage.setItem('refreshToken', res.data?.token?.RefreshToken)
                        localStorage.setItem('user', JSON.stringify(res?.data?.user))

                        setUser(res?.data?.user);

                        const roleName = res?.data?.user?.role?.roleName || "";
                        const isSuperAdmin =
                            roleName.trim().toLowerCase() === "super admin";

                        isSuperAdmin
                            ? navigate("/admin-management")
                            : navigate("/dashboard");

                        setLoginLoading(false);
                    } else {
                        setLoginLoading(false);
                    }
                }
            })
            .catch((error) => {
                const message =
                    error?.response?.data?.error ||
                    error?.response?.data?.message ||
                    en_ja["Something went wrong. Please try again."];

                setSnackbarMessage(message);
                setSnackbarOpen(true);
                setSnackbarType("error");

                setLoginLoading(false);
            });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            if (currentScreen === 'login') {
                const obj = {
                    username: formData.username,
                    password: formData.password
                }
                setLoginLoading(true);
                loginUser(obj);

            } else if (currentScreen === 'forgotPassword') {
                const obj = { email: formData.username };

                api.post("v1/admin/forgot-password", obj)
                    .then((res) => {
                        if (res?.data?.message) {
                            setSnackbarMessage(res?.data?.message);
                            setSnackbarOpen(true);
                            setSnackbarType('success');
                            setCurrentScreen('enterCodeAndChangePassword');
                        } else {
                            setSnackbarMessage(en_ja["Incorrect details"]);
                            setSnackbarOpen(true);
                            setSnackbarType('error');
                        }
                    })
                    .catch((error) => {
                        const message =
                            error?.response?.data?.error ||
                            error?.response?.data?.message ||
                            en_ja["Something went wrong. Please try again."];
                        setSnackbarMessage(message);
                        setSnackbarOpen(true);
                        setSnackbarType("error");
                    });

            } else if (currentScreen === 'enterCodeAndChangePassword') {
                const codeString = formData.code.join('');
                const obj = {
                    username: formData.username,
                    code: codeString,
                    newPassword: formData.newPassword,
                    confirmPassword: formData.confirmPassword,
                }

                api.post("/v1/admin/confirm-forgot-password", obj)
                    .then((res) => {
                        if (res?.data?.message) {
                            setSnackbarMessage(res?.data?.message);
                            setSnackbarOpen(true);
                            setSnackbarType('success');
                            setTimeout(() => {
                                setCurrentScreen('login');
                                setFormData({
                                    username: '',
                                    password: '',
                                    rememberMe: false,
                                    email: '',
                                    code: ['', '', '', '', '', ''],
                                    newPassword: '',
                                    confirmPassword: ''
                                });
                            }, 2000);
                        } else {
                            setSnackbarMessage(en_ja['Password could not be updated!']);
                            setSnackbarOpen(true);
                            setSnackbarType('error');
                        }
                    })
                    .catch((error) => {
                        const message =
                            error?.response?.data?.error ||
                            error?.response?.data?.message ||
                            en_ja["Something went wrong. Please try again."];
                        setSnackbarMessage(message);
                        setSnackbarOpen(true);
                        setSnackbarType("error");
                    });
            }
        }
    };

    const handleTogglePasswordVisibility = () => {
        setShowPassword(prev => !prev);
    };

    const handleToggleNewPasswordVisibility = () => {
        setShowNewPassword(prev => !prev);
    };

    const handleToggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(prev => !prev);
    };

    const handleForgotPassword = () => {
        setCurrentScreen('forgotPassword');
        setFormData(prev => ({
            ...prev,
            email: ''
        }));
    };

    const handleBackToLogin = () => {
        setCurrentScreen('login');
        setErrors({});
    };

    const handleBackToForgotPassword = () => {
        setCurrentScreen('forgotPassword');
        setErrors({});
    };

    const handleBackToEnterCodeAndChangePassword = () => {
        setCurrentScreen('enterCodeAndChangePassword');
        setErrors({});
    };

    const renderLoginForm = () => (
        <>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 700, color: "#1a1a1a" }}>
                {en_ja["Welcome Back"]}
            </Typography>

            <Typography variant="body1" sx={{ mb: 4, color: '#666666', fontSize: '14px', lineHeight: 1.5 }}>
                {en_ja["Enter your username and password to access your account"]}
            </Typography>

            <form onSubmit={handleSubmit}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 500, fontSize: '14px' }}>
                        {en_ja["Username"]}
                    </Typography>
                    <TextField
                        fullWidth
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        placeholder={en_ja["Enter your Username"]}
                        variant="outlined"
                        error={!!errors.username}
                        helperText={errors.username}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                fontSize: '14px',
                                '& fieldset': {
                                    borderColor: '#e0e0e0',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#ff0000',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#ff0000',
                                },
                            },
                        }}
                    />
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: '#1a1a1a', fontWeight: 500, fontSize: '14px' }}>
                        {en_ja["Password"]}
                    </Typography>
                    <TextField
                        fullWidth
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder={en_ja["Enter your password"]}
                        variant="outlined"
                        error={!!errors.password}
                        helperText={errors.password}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleTogglePasswordVisibility} edge="end" aria-label="toggle password visibility" size="small">
                                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                fontSize: '14px',
                                '& fieldset': {
                                    borderColor: '#e0e0e0',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#ff0000',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#ff0000',
                                },
                            },
                        }}
                    />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <FormControlLabel
                        control={<Checkbox name="rememberMe" checked={formData.rememberMe} onChange={handleInputChange} size="small"
                            sx={{ color: '#666', '&.Mui-checked': { color: '#ff0000' } }} />}
                        label={en_ja["Remember me"]}
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px', color: '#666666' } }}
                    />
                    <Link onClick={handleForgotPassword} variant="body2"
                        sx={{
                            color: '#ff0000',
                            textDecoration: 'none',
                            fontSize: '14px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}>
                        {en_ja["Forgot Password"]}
                    </Link>
                </Box>

                <Button type="submit" fullWidth variant="contained" disabled={loginLoading}
                    sx={{
                        py: 1.5,
                        backgroundColor: '#ff0000',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '16px',
                        fontWeight: 600,
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: '#e60000',
                            boxShadow: 'none',
                        },
                        mb: 4
                    }}>
                    {loginLoading ? en_ja["Signing In ....."] : en_ja["Sign In"]}
                </Button>

                {/* <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 4
                    }}
                >
                    <Link
                        href="#"
                        variant="body2"
                        sx={{
                            color: '#999999',
                            textDecoration: 'none',
                            fontSize: '12px',
                            '&:hover': {
                                textDecoration: 'underline',
                                color: '#666666'
                            },
                        }}
                    >
                        Terms & Conditions
                    </Link>
                    <Link
                        href="#"
                        variant="body2"
                        sx={{
                            color: '#999999',
                            textDecoration: 'none',
                            fontSize: '12px',
                            '&:hover': {
                                textDecoration: 'underline',
                                color: '#666666'
                            },
                        }}
                    >
                        Privacy Policy
                    </Link>
                </Box> */}
            </form>
        </>
    );

    const renderForgotPasswordForm = () => (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton
                    onClick={handleBackToLogin}
                    sx={{ mr: 1, p: 0.5 }}
                >
                    <ArrowBack fontSize="small" />
                </IconButton>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        color: '#1a1a1a',
                        fontSize: { xs: '1.5rem', sm: '1.75rem' },
                        flex: 1
                    }}
                >
                    {en_ja["Enter email"]}
                </Typography>
            </Box>

            <Typography
                variant="body1"
                sx={{
                    mb: 4,
                    color: '#666666',
                    fontSize: '14px',
                    lineHeight: 1.5
                }}
            >
                {en_ja["Enter your email to reset your password"]}
            </Typography>

            <form onSubmit={handleSubmit}>
                <Box sx={{ mb: 3 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            mb: 1,
                            color: '#1a1a1a',
                            fontWeight: 500,
                            fontSize: '14px'
                        }}
                    >
                        {en_ja["Username"]}
                    </Typography>
                    <TextField
                        fullWidth
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        placeholder={en_ja["Enter your Username"]}
                        variant="outlined"
                        error={!!errors.username}
                        helperText={errors.username}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                fontSize: '14px',
                                backgroundColor: '#f8f9fa',
                                '& fieldset': { borderColor: '#e0e0e0' },
                                '&:hover fieldset': { borderColor: '#ff0000' },
                                '&.Mui-focused fieldset': { borderColor: '#ff0000' },
                            },
                        }}
                    />
                </Box>

                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{
                        py: 1.5,
                        backgroundColor: '#ff0000',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '16px',
                        fontWeight: 600,
                        boxShadow: 'none',
                        '&:hover': { backgroundColor: '#e60000', boxShadow: 'none' },
                        mb: 4
                    }}
                >
                    {en_ja["Get Code"]}
                </Button>
            </form>
        </>
    );

    const renderEnterCodeAndChangePasswordForm = () => (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton
                    onClick={handleBackToForgotPassword}
                    sx={{ mr: 1, p: 0.5 }}
                >
                    <ArrowBack fontSize="small" />
                </IconButton>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        color: '#1a1a1a',
                        fontSize: { xs: '1.5rem', sm: '1.75rem' },
                        flex: 1
                    }}
                >
                    {en_ja["Reset Password"]}
                </Typography>
                <IconButton
                    onClick={handleBackToLogin}
                    sx={{ p: 0.5 }}
                >
                    <Close fontSize="small" />
                </IconButton>
            </Box>

            <Typography
                variant="body1"
                sx={{
                    mb: 4,
                    color: '#666666',
                    fontSize: '14px',
                    lineHeight: 1.5
                }}
            >
                {en_ja["Enter the verification code sent to your email and set your new password"]}
            </Typography>

            <form onSubmit={handleSubmit}>
                {/* Verification Code Section */}
                <Box sx={{ mb: 3 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            mb: 2,
                            color: '#1a1a1a',
                            fontWeight: 500,
                            fontSize: '14px'
                        }}
                    >
                        {en_ja["Verification Code"]}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, justifyContent: 'center' }}>
                        {formData.code.map((digit, index) => (
                            <TextField
                                key={index}
                                id={`code-input-${index}`}
                                value={digit}
                                onChange={(e) => handleCodeChange(index, e.target.value)}
                                variant="outlined"
                                error={!!errors.code}
                                inputProps={{
                                    maxLength: 1,
                                    style: { textAlign: 'center', fontSize: '18px', fontWeight: 600 }
                                }}
                                sx={{
                                    width: '50px',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        '& fieldset': { borderColor: errors.code ? '#d32f2f' : '#e0e0e0' },
                                        '&:hover fieldset': { borderColor: '#ff0000' },
                                        '&.Mui-focused fieldset': { borderColor: '#ff0000' },
                                    },
                                }}
                            />
                        ))}
                    </Box>
                    {errors.code && (
                        <Typography
                            variant="caption"
                            sx={{ color: '#d32f2f', display: 'block', textAlign: 'center', mb: 2 }}
                        >
                            {errors.code}
                        </Typography>
                    )}
                </Box>

                {/* New Password Section */}
                <Box sx={{ mb: 3 }}>
                    <Typography
                        variant="body2"
                        sx={{ mb: 1, color: '#1a1a1a', fontWeight: 500, fontSize: '14px' }}
                    >
                        {en_ja["New Password"]}
                    </Typography>
                    <TextField
                        fullWidth
                        name="newPassword"
                        type={showNewPassword ? 'text' : 'password'}
                        value={formData.newPassword}
                        onChange={handleInputChange}
                        placeholder={en_ja["Enter your new password"]}
                        variant="outlined"
                        error={!!errors.newPassword}
                        helperText={errors.newPassword}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleToggleNewPasswordVisibility}
                                        edge="end"
                                        aria-label="toggle new password visibility"
                                        size="small"
                                    >
                                        {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                fontSize: '14px',
                                '& fieldset': { borderColor: '#e0e0e0' },
                                '&:hover fieldset': { borderColor: '#ff0000' },
                                '&.Mui-focused fieldset': { borderColor: '#ff0000' },
                            },
                        }}
                    />
                </Box>

                {/* Confirm Password Section */}
                <Box sx={{ mb: 4 }}>
                    <Typography
                        variant="body2"
                        sx={{ mb: 1, color: '#1a1a1a', fontWeight: 500, fontSize: '14px' }}
                    >
                        {en_ja["Confirm Password"]}
                    </Typography>
                    <TextField
                        fullWidth
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        placeholder={en_ja["Confirm your new password"]}
                        variant="outlined"
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleToggleConfirmPasswordVisibility}
                                        edge="end"
                                        aria-label="toggle confirm password visibility"
                                        size="small"
                                    >
                                        {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                fontSize: '14px',
                                '& fieldset': { borderColor: '#e0e0e0' },
                                '&:hover fieldset': { borderColor: '#ff0000' },
                                '&.Mui-focused fieldset': { borderColor: '#ff0000' },
                            },
                        }}
                    />
                </Box>

                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{
                        py: 1.5,
                        backgroundColor: '#ff0000',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '16px',
                        fontWeight: 600,
                        boxShadow: 'none',
                        '&:hover': { backgroundColor: '#e60000', boxShadow: 'none' },
                        mb: 4
                    }}
                >
                    {en_ja["Reset Password"]}
                </Button>
            </form>
        </>
    );

    return (
        <ThemeProvider theme={theme}>
            <CustomSnackbar isOpen={snackbarOpen} message={snackbarMessage} onClose={handleCloseSnackbar} type={snackbarType} />
            <Box
                sx={{
                    minHeight: '100vh',
                    backgroundColor: '#f5f7fa',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: { xs: 20, md: 40 },
                        left: { xs: 20, md: 40 },
                        zIndex: 2
                    }}
                >
                    <img src={LogoImage} alt={en_ja["Shop Japan Logo"]} style={{ height: '40px', width: 'auto' }} />
                </Box>
                <Box
                    sx={{
                        position: 'absolute',
                        left: { xs: '-100px', sm: '80px', md: '150px', lg: '200px' },
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 1,
                        display: { xs: 'none', sm: 'block' }
                    }}
                >
                    <img src={HeroImage} alt={en_ja["Medical Illustration"]} style={{ width: '400px', height: 'auto', maxWidth: '90vw' }} />
                </Box>
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '220px',
                        zIndex: 0
                    }}
                >
                    <img src={DesignImage} alt={en_ja["Decorative Pattern"]} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 1 }} />
                </Box>
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' }, alignItems: 'center', minHeight: '100vh', pr: { md: 8, lg: 12 } }}>
                        <Box sx={{ width: "100%", maxWidth: "420px", display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, color: "#1a1a1a", textAlign: "center" }}>
                                {en_ja["SJ Admin Portal"]}
                            </Typography>
                            <Card elevation={16} sx={{ p: { xs: 4, sm: 5 }, borderRadius: 3, backgroundColor: 'white', boxShadow: '0px 16px 40px rgba(0, 0, 0, 0.12)', width: '100%', maxWidth: '420px', border: '1px solid rgba(0, 0, 0, 0.05)' }}>
                                <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 3 }}>
                                    <img src={LogoImage} alt={en_ja["Shop Japan Logo"]} style={{ height: '32px', width: 'auto' }} />
                                </Box>
                                {currentScreen === 'login' && renderLoginForm()}
                                {currentScreen === 'forgotPassword' && renderForgotPasswordForm()}
                                {currentScreen === 'enterCodeAndChangePassword' && renderEnterCodeAndChangePasswordForm()}
                            </Card>
                        </Box>
                    </Box>
                </Container>
            </Box>
        </ThemeProvider>
    );
};

export default Login;